import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Icone, type NomIcone } from './Icone';

export interface ElementMenu {
  libelle: string;
  icone?: NomIcone;
  /** Lien de navigation… */
  vers?: string;
  /** …ou geste immédiat. */
  surClic?: () => void;
  desactive?: boolean;
  /** Raison d'une entrée désactivée, annoncée au survol. */
  aide?: string;
}

/**
 * Menu « … » d'une ligne (charte, doc 06) : un bouton discret de fin de
 * ligne, qui déroule les gestes possibles sur l'objet. Les groupes sont
 * séparés d'un filet ; un groupe vide disparaît, et le bouton avec lui si
 * plus rien n'est proposé.
 *
 * Le panneau est porté hors du tableau (portail, position fixe) : un
 * conteneur qui défile ne le rogne pas. Échap, un clic ailleurs ou un
 * défilement le referment.
 */
export function MenuActions({ libelle, groupes }: { libelle: string; groupes: ElementMenu[][] }) {
  const [ouvert, setOuvert] = useState(false);
  const [position, setPosition] = useState<{ haut: number; droite: number } | null>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const panneau = useRef<HTMLDivElement>(null);

  const pleins = groupes.filter((g) => g.length > 0);

  useLayoutEffect(() => {
    if (!ouvert || !bouton.current) return;
    const r = bouton.current.getBoundingClientRect();
    setPosition({ haut: r.bottom + 4, droite: window.innerWidth - r.right });
  }, [ouvert]);

  useEffect(() => {
    if (!ouvert) return;
    panneau.current
      ?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')
      ?.focus();
    const fermer = () => setOuvert(false);
    const surEchap = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOuvert(false);
        bouton.current?.focus();
      }
    };
    const surClic = (e: MouseEvent) => {
      const cible = e.target as Node;
      if (!panneau.current?.contains(cible) && !bouton.current?.contains(cible)) fermer();
    };
    document.addEventListener('keydown', surEchap);
    document.addEventListener('mousedown', surClic);
    window.addEventListener('scroll', fermer, true);
    window.addEventListener('resize', fermer);
    return () => {
      document.removeEventListener('keydown', surEchap);
      document.removeEventListener('mousedown', surClic);
      window.removeEventListener('scroll', fermer, true);
      window.removeEventListener('resize', fermer);
    };
  }, [ouvert]);

  if (pleins.length === 0) return null;

  return (
    <>
      <button
        ref={bouton}
        type="button"
        className="tableau__action"
        aria-label={libelle}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        onClick={() => setOuvert((o) => !o)}
      >
        <Icone nom="actions" taille={16} />
      </button>
      {ouvert &&
        position &&
        createPortal(
          <div
            ref={panneau}
            className="menu"
            role="menu"
            aria-label={libelle}
            style={{ top: position.haut, right: position.droite }}
          >
            {pleins.map((groupe, i) => (
              <div key={i} className="menu__groupe" role="group">
                {groupe.map((el) => {
                  const contenu = (
                    <>
                      {el.icone && <Icone nom={el.icone} taille={15} />}
                      <span>{el.libelle}</span>
                    </>
                  );
                  if (el.vers && !el.desactive) {
                    return (
                      <Link
                        key={el.libelle}
                        to={el.vers}
                        role="menuitem"
                        className="menu__element"
                        onClick={() => setOuvert(false)}
                      >
                        {contenu}
                      </Link>
                    );
                  }
                  return (
                    <button
                      key={el.libelle}
                      type="button"
                      role="menuitem"
                      className="menu__element"
                      aria-disabled={el.desactive || undefined}
                      title={el.desactive ? el.aide : undefined}
                      onClick={() => {
                        if (el.desactive) return;
                        setOuvert(false);
                        el.surClic?.();
                      }}
                    >
                      {contenu}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
